import React from 'react';
import { Category } from '../../../types';
import produce from 'immer';
import { DefaultCategoryPicker } from './DefaultCategoryPicker';
import { CategoryPicker } from './CategoryPicker';
import { FormGroup, ButtonTertiary, IconPlusAlt, Type } from '@spotify-internal/encore-creator-web';
import styled from 'styled-components';

const CategoryLimit = 3;

const AdditionalCategoryButton = styled(ButtonTertiary)`
  display: flex;
  align-items: center;
  padding-left: 0;
  padding-top: 0;
  text-transform: initial;
  letter-spacing: unset;

  & > p {
    margin-left: 8px;
  }
`;

export class PlaceholderCategory {
  key: number;
  value: string;

  constructor() {
    this.key = Date.now();
    this.value = 'String';
  }
}

export type CategoryFormData = Array<Category | PlaceholderCategory>;

interface Props {
  categories: Category[];
  value: CategoryFormData;
  onChange(changes: CategoryFormData): void;
}

export function CategoryForm(props: Props) {
  function handleSelectCategory(category: Category, index: number) {
    const changes = produce(props.value, draft => {
      draft.splice(index, 1, category);
    });

    props.onChange(changes);
  }

  function handleRemoveCategory(index: number) {
    const changes = produce(props.value, draft => {
      draft.splice(index, 1);
    });

    props.onChange(changes);
  }

  if (props.value.length === 0) {
    return (
      <div data-testid="category-picker-initial-view">
        <DefaultCategoryPicker
          categories={props.categories}
          onSelectCategory={category => handleSelectCategory(category, 0)}
          label="Primary category"
          id="first-category"
          required
        />
      </div>
    );
  }

  return (
    <div data-testid="category-picker-with-selected">
      {props.value.map((category, index) => {
        const label = index === 0 ? 'Primary category' : 'Additional category';

        const filteredCategories = props.categories.filter(primaryCategory => {
          /**
           * We want to keep the matching category. If I select "Educational" as a primary
           * category it should still appear in the dropdown of choices.
           */
          if (primaryCategory.value === category.value) {
            return true;
          }

          /**
           * Remove categories that have already been chosen.
           */
          return !props.value.find(cat => cat.value === primaryCategory.value);
        });

        if (category instanceof PlaceholderCategory) {
          return (
            <DefaultCategoryPicker
              key={category.key}
              label={label}
              id={category.key.toString()}
              categories={filteredCategories}
              onSelectCategory={newCategory => handleSelectCategory(newCategory, index)}
              onRemove={() => void handleRemoveCategory(index)}
            />
          );
        }

        return (
          <CategoryPicker
            key={category.value}
            label={label}
            id={category.value.toString()}
            categories={filteredCategories}
            selectedCategory={category}
            onChangeCategory={newCategory => handleSelectCategory(newCategory, index)}
            selectedSubcategories={new Set(category.subcategories)}
            onRemove={index === 0 ? undefined : () => void handleRemoveCategory(index)}
            required={index === 0}
          />
        );
      })}

      {props.value.length < CategoryLimit && (
        <FormGroup>
          <AdditionalCategoryButton
            data-testid="add-category-button"
            onClick={() => props.onChange(props.value.concat(new PlaceholderCategory()))}
          >
            <IconPlusAlt aria-label="IconPlusAlt" iconSize={32} />
            <Type.p condensed> Add an additional category </Type.p>
          </AdditionalCategoryButton>
        </FormGroup>
      )}
    </div>
  );
}
